import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AdminStudentPlatformHeaderComponent } from '../admin-student-platform-header/admin-student-platform-header.component';
import { AdminStudentPlatformService } from '@edtech-ui/core/services/admin-student-platform.service';
import { AdminUser, EnrollmentCourse, QuizAttempt, QuizAttemptedUser, QuizAttemptsDialogData } from '@edtech-ui/core/models/admin-student-platform/admin-user-management';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DialogCourseData, DialogEnrollmentsComponent } from '../../admin-student-platform/dialog-enrollments/dialog-enrollments.component';
import { DialogQuizAttemptsComponent } from '../dialog-quiz-attempts/dialog-quiz-attempts.component';
import { userManagementGetUsersEnum } from '@edtech-ui/constants/user-managemnt-get-users-status.enum';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { AdminDashboardStats, PlatformDashboardStats } from '@edtech-ui/core/models/admin-student-platform/admin-student-dashboard';
import { CompanyService } from '@edtech-ui/core/services/company-management/company.service';

interface StatCard {
	title: string;
	value: string | number;
	subtitle?: string;
	icon: string;
	iconBg: string;
}

@Component({
	selector: 'app-admin-student-platform-user-management',
	standalone: true,
	imports: [CommonModule, FormsModule, AdminStudentPlatformHeaderComponent, MatTooltipModule,
		DialogEnrollmentsComponent, DialogQuizAttemptsComponent],
	templateUrl: './admin-student-platform-user-management.component.html',
	styleUrl: './admin-student-platform-user-management.component.scss'
})
export class AdminStudentPlatformUserManagementComponent {

	Math = Math;

	searchTerm = '';
	currentPage = 1;
	pageSize = 10;

	totalUsers = 0;
	totalEnrollments = 0;
	totalQuizzes = 0;

	isLoading = false;
	isExporting = false;
	openMenuIndex: number | null = null;

	users: any[] = [];
	enrollments: EnrollmentCourse[] = [];
	quizzes: QuizAttempt[] = [];

	searchTimeout: string | null = null;
	search$ = new Subject<string>();

	showUserDialog = false;
	selectedUser: any = null;

	showCourseDialog = false;
	selectedCourse: DialogCourseData | null = null;
	isCourseLoading = false;

	showQuizDialog = false;
	selectedQuiz: QuizAttemptsDialogData | null = null;
	isQuizLoading = false;
	private destroy$ = new Subject<void>();
	userManagementGetUsersEnum = userManagementGetUsersEnum;
	PermissionForStudents = PermissionForStudents;

	allowedTabs: string[] = [];
	activeTab!: string;

	tabPermissionMap: Record<string, string> = {
		users: PermissionForStudents.USER_MANAGEMENT_VIEW,
		enrollments: PermissionForStudents.USER_MANAGEMENT_VIEW_ENROLLMENTS,
		quizzes: PermissionForStudents.USER_MANAGEMENT_VIEW_QUIZZES
	};
	userPaymentStatus = 'all';
	dateFrom = '';
	dateTo = '';


	currentEnrollPage = 1;
	pageEnrollSize = 10;
	totalEnrollPages = 0;
	selectedCourseId = '';
	showDatePicker = false;
	pickerStartDate: Date | null = null;
	pickerEndDate: Date | null = null;
	pickerHoverDate: Date | null = null;
	pickerViewDate: Date = new Date();

	showStatusDropdown = false;
	stats: StatCard[] = [];
	companyStats: StatCard[] = [];
	approvedCompanies: any[] = [];
	selectedCompanyId: string = 'all';
	platformStats: PlatformDashboardStats | null = null;

	showStatsDatePicker = false;
	statsDateFrom = '';
	statsDateTo = '';
	statsPickerStartDate: Date | null = null;
	statsPickerEndDate: Date | null = null;
	statsPickerHoverDate: Date | null = null;
	statsPickerViewDate: Date = new Date();

	statsPickerCalendarDays: (Date | null)[] = [];

	constructor(
		private adminService: AdminStudentPlatformService,
		private _authService: AuthServiceService,
		private router: Router,
		private companyService: CompanyService

	) { }

	ngOnInit() {
		this._generateCalendarDays();
		this.setAllowedTabs();
		this.generateStatsCalendar();

		this.search$
			.pipe(
				debounceTime(200),
				distinctUntilChanged(),
				takeUntil(this.destroy$)
			)
			.subscribe(() => {
				this.currentPage = 1;
				if (this.activeTab === 'users' && this._authService.hasPermission(PermissionForStudents.USER_MANAGEMENT_VIEW)) {
					this.loadUsers();
				}
				if (this.activeTab === 'enrollments' && this._authService.hasPermission(PermissionForStudents.USER_MANAGEMENT_VIEW_ENROLLMENTS)) {
					this.loadEnrollments();
				}
				if (this.activeTab === 'quizzes' && this._authService.hasPermission(PermissionForStudents.USER_MANAGEMENT_VIEW_QUIZZES)) {
					this.loadQuizAttempts();
				}

			});

		this.setDefaultStatsDate();
		this.loadDashboardStats();
		// this.loadApprovedCompanies();
		this.loadPlatformStats();
	}

	setAllowedTabs() {
		this.allowedTabs = Object.keys(this.tabPermissionMap).filter(tab =>
			this._authService.hasPermission(this.tabPermissionMap[tab])
		);

		if (this.allowedTabs.length > 0) {
			this.activeTab = this.allowedTabs[0];
			this.loadTabData(this.activeTab);
		}
	}

	setTab(tab: 'users' | 'enrollments' | 'quizzes') {
		if (!this.allowedTabs.includes(tab)) return;

		this.activeTab = tab;
		this.currentPage = 1;
		this.openMenuIndex = null;
		this.searchTerm = '';
		this.dateFrom = '';
		this.dateTo = '';

		this.loadTabData(tab);
	}

	loadTabData(tab: string) {
		switch (tab) {
			case 'users':
				this.loadUsers();
				break;
			case 'enrollments':
				this.loadEnrollments();
				break;
			case 'quizzes':
				this.loadQuizAttempts();
				break;
		}
	}

	allUsersCache: any[] = [];

	loadUsers() {
		this.isLoading = true;

		this.adminService
			.getUsers(this.currentPage, this.pageSize, this.searchTerm, userManagementGetUsersEnum.ACTIVE, this.userPaymentStatus, this.dateFrom || undefined, this.dateTo || undefined)
			.pipe(takeUntil(this.destroy$))
			.subscribe(res => {
				const mappedUsers = res.users.map((u: AdminUser) => ({
					id: u.user_id,
					user_id: u.user_id,
					initials: this.getInitials(u.name),
					name: u.name,
					email: u.email,
					registered: u.registered,
					phone_number: u.phone_number,
					college_name: u.college_name,
					city: u.city,
					pincode: u.pincode,
					courses: u.enrolled_courses,
					quizzes: u.quiz_attempts,
					ai_interview_count: u.ai_interview_count,
					ai_mock_test_count: u.ai_mock_test_count,
					is_active: u.is_active,
					payment: u.payment_status ? u.payment_status.toLowerCase() : 'pending',
					registration_source: u.registration_source,
					last_login_source: u.last_login_source,
					last_login_at: u.last_login_at,
					mentor_sessions_booked: u.mentor_sessions_booked,
					job_applied_count: u.job_applied_count ?? (u as any).jobs_applied_count ?? 0
				}));

				this.users = mappedUsers;

				if (!this.searchTerm) {
					this.allUsersCache = mappedUsers;
				} else {
					mappedUsers.forEach(nu => {
						if (!this.allUsersCache.some(existing => (existing.user_id || existing.email) === (nu.user_id || nu.email))) {
							this.allUsersCache.push(nu);
						}
					});
				}

				this.totalUsers = res.total;
				this.isLoading = false;
			});
	}

	loadEnrollments() {
		this.isLoading = true;

		this.adminService
			.getEnrollments(
				null,
				this.currentPage,
				this.pageSize,
				this.searchTerm
			)
			.subscribe(res => {
				this.enrollments = res.Data.courses;
				this.totalEnrollments = res.Data.total;
				this.pageSize = res.Data.limit;
				this.isLoading = false;
			});
	}


	loadQuizAttempts() {
		this.isLoading = true;

		this.adminService
			.getQuizAttempts(
				null,
				this.currentPage,
				this.pageSize,
				this.searchTerm
			)
			.subscribe(res => {
				this.quizzes = res.Data.quizzes;
				this.totalQuizzes = res.Data.total;
				this.pageSize = res.Data.limit;
				this.isLoading = false;
			});
	}


	onSearchInput() {
		this.search$.next(this.searchTerm);
	}


	get totalRecords(): number {
		if (this.activeTab === 'users') return this.totalUsers;
		if (this.activeTab === 'enrollments') return this.totalEnrollments;
		return this.totalQuizzes;
	}


	get totalPages(): number {
		return Math.ceil(this.totalRecords / this.pageSize);
	}


	changePage(page: number) {
		if (page < 1 || page > this.totalPages) return;
		this.currentPage = page;
		if (this.activeTab === 'users') this.loadUsers();
		if (this.activeTab === 'enrollments') this.loadEnrollments();
		if (this.activeTab === 'quizzes') this.loadQuizAttempts();
	}


	getEndIndex(): number {
		return Math.min(this.currentPage * this.pageSize, this.totalRecords);
	}


	getInitials(name: string): string {
		return name
			.split(' ')
			.map(n => n[0])
			.join('')
			.toUpperCase();
	}


	clearSearch() {
		this.searchTerm = '';
		this.currentPage = 1;
		this.search$.next('');
	}


	toggleStatusDropdown(event: Event) {
		event.stopPropagation();
		this.showStatusDropdown = !this.showStatusDropdown;
	}


	selectStatus(status: string) {
		this.userPaymentStatus = status;
		this.showStatusDropdown = false;
		this.onStatusChange();
	}


	get paginatedUsers() {
		if (!this.searchTerm || !this.searchTerm.trim()) {
			return this.users;
		}


		const rawTerm = this.searchTerm.trim().toLowerCase();
		const digitsOnlyTerm = rawTerm.replace(/\D/g, '');

		// Combine current API results and cached users pool so partial typing never loses entries
		const pool = [...this.users];
		this.allUsersCache.forEach(cached => {
			if (!pool.some(p => (p.user_id || p.email) === (cached.user_id || cached.email))) {
				pool.push(cached);
			}
		});

		return pool.filter(u => {
			// 1. Phone number matching (both clean digits and string checks)
			const rawPhone = String(u.phone_number || '').toLowerCase();
			const cleanPhone = rawPhone.replace(/\D/g, '');

			if (digitsOnlyTerm.length > 0 && cleanPhone.includes(digitsOnlyTerm)) {
				return true;
			}
			if (rawPhone.includes(rawTerm)) {
				return true;
			}

			// 2. Text field matching
			const nameMatch = u.name && u.name.toLowerCase().includes(rawTerm);
			const emailMatch = u.email && u.email.toLowerCase().includes(rawTerm);
			const collegeMatch = u.college_name && u.college_name.toLowerCase().includes(rawTerm);
			const cityMatch = u.city && u.city.toLowerCase().includes(rawTerm);
			const pincodeMatch = u.pincode && String(u.pincode).includes(rawTerm);

			return nameMatch || emailMatch || collegeMatch || cityMatch || pincodeMatch;
		});
	}


	openUserDetails(user: any) {
		const selectedUser = {
			name: user.name,
			email: user.email,
			registered: user.registered,
			phone_number: user.phone_number,
			college_name: user.college_name,
			city: user.city,
			pincode: user.pincode,
			enrolledCourses: user.courses,
			quizAttempts: user.quizzes,
			ai_interview_count: user.ai_interview_count,
			ai_mock_test_count: user.ai_mock_test_count,
			paymentStatus: user.payment,
			is_active: user.is_active,
			registration_source: user.registration_source,
			last_login_source: user.last_login_source,
			last_login_at: user.last_login_at,
			mentor_sessions_booked: user.mentor_sessions_booked
		};

		const targetId = user.id || user.user_id || user.email || 'details';
		this.router.navigate(['/admin-student-platform/users', targetId], {
			state: { user: selectedUser }
		});
	}


	openCourseUsers(course: EnrollmentCourse) {
		this.showCourseDialog = true;
		this.isCourseLoading = true;
		this.selectedCourseId = course.course_id;

		this.adminService.getCourseEnrollments(this.selectedCourseId, this.currentEnrollPage, this.pageEnrollSize, '').subscribe(res => {
			const data = res.Data;

			this.currentEnrollPage = data.page;
			this.pageEnrollSize = data.limit;
			this.totalEnrollPages = data.total_pages;

			this.selectedCourse = {
				title: data.course_name,
				bundle: 'N/A',
				totalEnrollments: data.total,
				users: data.enrolled_users.map(u => ({
					name: u.name,
					email: u.email,
					enrolledDate: new Date(u.enrolled_date).toLocaleDateString(),
					progress: u.progress ?? 0,
					initials: this.getInitials(u.name)
				}))
			};

			this.isCourseLoading = false;
		});
	}

	openQuizAttempts(quiz: QuizAttempt) {
		this.showQuizDialog = true;
		this.isQuizLoading = true;
		this.selectedQuiz = null;

		this.adminService
			.getQuizAttemptsByQuizId(quiz.quiz_id, 1, 10, '')
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (res) => {

					if (!res.Success || !res.Data) {
						this.selectedQuiz = this.emptyQuizState(quiz);
						this.isQuizLoading = false;
						return;
					}
					const data = res.Data;
					this.selectedQuiz = {
						quizTitle: data.quiz_name,
						courseName: data.course_name,
						totalAttempts: data.total,
						users: (data.attempted_users ?? []).map((u: QuizAttemptedUser) => ({
							name: u.name,
							email: u.email,
							attemptDate: u.attempt_date
								? new Date(u.attempt_date).toLocaleDateString()
								: 'Not Attempted',
							score: u.score ?? 0,
							initials: this.getInitials(u.name)
						}))
					};

					this.isQuizLoading = false;
				},

				error: () => {
					this.selectedQuiz = this.emptyQuizState(quiz);
					this.isQuizLoading = false;
				}
			});
	}


	private emptyQuizState(quiz: QuizAttempt): QuizAttemptsDialogData {
		return {
			quizTitle: quiz.quiz_name,
			courseName: quiz.course_name,
			totalAttempts: 0,
			users: []
		};
	}


	@HostListener('document:click')
	closeMenu() {
		this.openMenuIndex = null;
	}


	onStatusChange() {
		this.currentPage = 1;
		this.loadUsers();
	}


	onDateFilterChange() {
		this.currentPage = 1;
		this.loadUsers();
	}


	exportUsers() {
		this.isExporting = true;
		this.adminService
			.exportUsers(
				this.searchTerm || undefined,
				userManagementGetUsersEnum.ALL,
				this.userPaymentStatus,
				this.dateFrom || undefined,
				this.dateTo || undefined
			).pipe(takeUntil(this.destroy$)).subscribe({
				next: (blob) => {
					const url = URL.createObjectURL(blob);
					const anchor = document.createElement('a');
					anchor.href = url;
					anchor.download = `users-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
					anchor.click();
					URL.revokeObjectURL(url);
					this.isExporting = false;
				},
				error: () => {
					this.isExporting = false;
				}
			});
	}


	changeEnrollPage(page: number) {
		if (page < 1 || page > this.totalEnrollPages) return;
		this.currentEnrollPage = page;
		this.adminService.getCourseEnrollments(
			this.selectedCourseId,
			this.currentEnrollPage,
			this.pageEnrollSize, '').subscribe(res => {
				const data = res.Data;
				this.currentEnrollPage = data.page;
				if (this.selectedCourse) {
					this.selectedCourse.users = data.enrolled_users.map(u => ({
						name: u.name,
						email: u.email,
						enrolledDate: new Date(u.enrolled_date).toLocaleDateString(),
						progress: u.progress ?? 0,
						initials: this.getInitials(u.name)
					}));
				}
			});
	}


	get pickerDisplayLabel(): string {
		if (!this.pickerStartDate) return 'Select date range';
		const fmt = (d: Date) => `${String(d.getDate()).padStart(2, '0')} ${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
		if (!this.pickerEndDate) return fmt(this.pickerStartDate);
		return `${fmt(this.pickerStartDate)} – ${fmt(this.pickerEndDate)}`;
	}


	get pickerMonthLabel(): string {
		return this.pickerViewDate.toLocaleString('default', { month: 'long', year: 'numeric' });
	}


	get pickerWeekDays(): string[] {
		return ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
	}


	pickerCalendarDays: (Date | null)[] = [];


	private _generateCalendarDays(): void {
		const year = this.pickerViewDate.getFullYear();
		const month = this.pickerViewDate.getMonth();
		const firstDay = new Date(year, month, 1).getDay();
		const daysInMonth = new Date(year, month + 1, 0).getDate();
		const cells: (Date | null)[] = [];
		for (let i = 0; i < firstDay; i++) cells.push(null);
		for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
		this.pickerCalendarDays = cells;
	}


	pickerPrevMonth(): void {
		const d = new Date(this.pickerViewDate);
		d.setMonth(d.getMonth() - 1);
		this.pickerViewDate = d;
		this._generateCalendarDays();
	}


	pickerNextMonth(): void {
		const d = new Date(this.pickerViewDate);
		d.setMonth(d.getMonth() + 1);
		this.pickerViewDate = d;
		this._generateCalendarDays();
	}


	pickerSelectDay(day: Date | null): void {
		if (!day) return;
		if (!this.pickerStartDate || (this.pickerStartDate && this.pickerEndDate)) {
			this.pickerStartDate = day;
			this.pickerEndDate = null;
		} else {
			if (day < this.pickerStartDate) {
				this.pickerEndDate = this.pickerStartDate;
				this.pickerStartDate = day;
			} else {
				this.pickerEndDate = day;
			}
		}
	}


	pickerIsStart(day: Date | null): boolean {
		if (!day || !this.pickerStartDate) return false;
		return this._sameDay(day, this.pickerStartDate);
	}


	pickerIsEnd(day: Date | null): boolean {
		if (!day || !this.pickerEndDate) return false;
		return this._sameDay(day, this.pickerEndDate);
	}


	pickerIsInRange(day: Date | null): boolean {
		if (!day || !this.pickerStartDate) return false;
		const end = this.pickerEndDate || this.pickerHoverDate;
		if (!end) return false;
		const s = this.pickerStartDate < end ? this.pickerStartDate : end;
		const e = this.pickerStartDate < end ? end : this.pickerStartDate;
		return day >= s && day <= e;
	}


	pickerIsToday(day: Date | null): boolean {
		if (!day) return false;
		return this._sameDay(day, new Date());
	}


	private _sameDay(a: Date, b: Date): boolean {
		return a.getFullYear() === b.getFullYear() &&
			a.getMonth() === b.getMonth() &&
			a.getDate() === b.getDate();
	}


	private _toDateStr(d: Date): string {
		return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
	}


	applyDatePicker(): void {
		if (this.pickerStartDate) {
			this.dateFrom = this._toDateStr(this.pickerStartDate);
			this.dateTo = this.pickerEndDate
				? this._toDateStr(this.pickerEndDate)
				: this._toDateStr(this.pickerStartDate);
		}
		this.showDatePicker = false;
		this.onDateFilterChange();
	}


	clearDatePicker(): void {
		this.pickerStartDate = null;
		this.pickerEndDate = null;
		this.pickerHoverDate = null;
		this.dateFrom = '';
		this.dateTo = '';
		this.showDatePicker = false;
		this.onDateFilterChange();
	}


	toggleDatePicker(event: MouseEvent): void {
		event.stopPropagation();
		this.showStatsDatePicker = false;
		this.showDatePicker = !this.showDatePicker;
	}


	onCompanyChange(): void {
		this.loadPlatformStats(this.selectedCompanyId);
	}


	private hasValue(value: unknown): boolean {
		return value !== null && value !== undefined;
	}


	private mapStatsToCards(data: AdminDashboardStats): StatCard[] {
		const stats: StatCard[] = [];

		if (this.hasValue(data.total_users)) {
			stats.push({
				title: 'Total Registered Users',
				value: data.total_users,
				subtitle: '',
				icon: 'group',
				iconBg: 'bg-purple'
			});
		}
		return stats;
	}


	updateCompanyStats(): void {
		const data = this.platformStats;
		this.companyStats = [
			{
				title: 'Total Candidates Applied',
				value: data?.total_candidates_applied ?? 0,
				icon: 'person_add',
				iconBg: 'bg-orange'
			},
			{
				title: 'Jobs Posted on BeExpert',
				value: data?.jobs_posted_on_beexpert ?? 0,
				icon: 'list_alt',
				iconBg: 'bg-green'
			}];
	}


	loadApprovedCompanies(): void {
		this.companyService.getAllCompaniesList({ status: 'approved', limit: 100 }).subscribe({
			next: (res) => {
				if (res.Success && res.Data && res.Data.companies) {
					this.approvedCompanies = res.Data.companies;
				}
				this.updateCompanyStats();
				this.checkLoadingState();
			},
			error: () => {
				this.updateCompanyStats();
				this.checkLoadingState();
			}
		});
	}


	loadDashboardStats(): void {
		const payload = {
			from_date: this.statsDateFrom,
			to_date: this.statsDateTo
		};
		this.adminService.getDashboardStats(payload).subscribe({
			next: (data) => {
				this.stats = this.mapStatsToCards(data);
				this.checkLoadingState();
			},
			error: () => {
				this.checkLoadingState();
			}
		});
	}


	checkLoadingState(): void {
		if (this.stats.length > 0 || this.companyStats.length > 0) {
			this.isLoading = false;
		}
	}


	loadPlatformStats(companyId?: string): void {
		const payload = {
			from_date: this.statsDateFrom,
			to_date: this.statsDateTo
		};
		this.adminService.getPlatformStats(companyId, payload).subscribe({
			next: (data) => {
				if (data) {
					this.platformStats = data;
					this.updateCompanyStats();
				}
				this.checkLoadingState();
			},
			error: () => {
				this.checkLoadingState();
			}
		});
	}



	toggleStatsDatePicker(event: MouseEvent): void {
		event.stopPropagation();
		this.showDatePicker = false;
		this.showStatsDatePicker = !this.showStatsDatePicker;
	}


	private generateStatsCalendar(): void {
		const year = this.statsPickerViewDate.getFullYear();
		const month = this.statsPickerViewDate.getMonth();
		const firstDay = new Date(year, month, 1).getDay();
		const daysInMonth = new Date(year, month + 1, 0).getDate();
		const cells: (Date | null)[] = [];
		for (let i = 0; i < firstDay; i++) {
			cells.push(null);
		}
		for (let d = 1; d <= daysInMonth; d++) {
			cells.push(new Date(year, month, d));
		}
		this.statsPickerCalendarDays = cells;
	}


	statsPrevMonth(): void {
		const d = new Date(this.statsPickerViewDate);
		d.setMonth(d.getMonth() - 1);
		this.statsPickerViewDate = d;
		this.generateStatsCalendar();
	}

	statsNextMonth(): void {
		const d = new Date(this.statsPickerViewDate);
		d.setMonth(d.getMonth() + 1);
		this.statsPickerViewDate = d;
		this.generateStatsCalendar();
	}


	get statsMonthLabel(): string {
		return this.statsPickerViewDate.toLocaleString('default', {
			month: 'long',
			year: 'numeric'
		});
	}


	get statsDisplayLabel(): string {
		if (!this.statsPickerStartDate) {
			return 'Custom Date Range';
		}
		const format = (d: Date) => `${String(d.getDate()).padStart(2, '0')} ${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
		if (!this.statsPickerEndDate) {
			return format(this.statsPickerStartDate);
		}
		return `${format(this.statsPickerStartDate)} - ${format(this.statsPickerEndDate)}`;
	}


	statsSelectDay(day: Date | null): void {
		if (!day) return;
		if (!this.statsPickerStartDate || (this.statsPickerStartDate && this.statsPickerEndDate)) {
			this.statsPickerStartDate = day;
			this.statsPickerEndDate = null;
		} else {
			if (day < this.statsPickerStartDate) {
				this.statsPickerEndDate = this.statsPickerStartDate;
				this.statsPickerStartDate = day;
			} else {
				this.statsPickerEndDate = day;
			}
		}
	}


	statsIsStart(day: Date | null): boolean {
		return !!(day && this.statsPickerStartDate && this._sameDay(day, this.statsPickerStartDate));
	}


	statsIsEnd(day: Date | null): boolean {
		return !!(day && this.statsPickerEndDate && this._sameDay(day, this.statsPickerEndDate));
	}


	statsIsToday(day: Date | null): boolean {
		return !!day && this._sameDay(day, new Date());
	}


	statsIsInRange(day: Date | null): boolean {

		if (!day || !this.statsPickerStartDate) {
			return false;
		}
		const end = this.statsPickerEndDate || this.statsPickerHoverDate;
		if (!end) {
			return false;
		}
		const start = this.statsPickerStartDate < end ? this.statsPickerStartDate : end;
		const finish = this.statsPickerStartDate < end ? end : this.statsPickerStartDate;
		return day >= start && day <= finish;
	}


	applyStatsDatePicker(): void {

		if (this.statsPickerStartDate) {
			this.statsDateFrom = this._toDateStr(this.statsPickerStartDate);
			this.statsDateTo = this.statsPickerEndDate
				? this._toDateStr(this.statsPickerEndDate)
				: this.statsDateFrom;
		}
		this.showStatsDatePicker = false;
		this.loadPlatformStats(this.selectedCompanyId);
		this.loadDashboardStats();
	}


	clearStatsDatePicker(): void {
		const today = new Date();
		this.statsPickerStartDate = today;
		this.statsPickerEndDate = today;
		this.statsPickerHoverDate = null;
		this.statsDateFrom = this._toDateStr(today);
		this.statsDateTo = this._toDateStr(today);
		this.showStatsDatePicker = false;
		this.loadDashboardStats();
		this.loadPlatformStats(this.selectedCompanyId);
	}


	private setDefaultStatsDate(): void {
		const today = new Date();
		this.statsPickerStartDate = today;
		this.statsPickerEndDate = today;
		this.statsDateFrom = this._toDateStr(today);
		this.statsDateTo = this._toDateStr(today);
	}


	get statsFilterLabel(): string {
		if (!this.statsPickerStartDate) {
			return 'Today';
		}
		const today = new Date();
		if (this._sameDay(this.statsPickerStartDate, today) && !this.statsPickerEndDate) {
			return 'Today';
		}
		if (this._sameDay(this.statsPickerStartDate, today) && this.statsPickerEndDate && this._sameDay(this.statsPickerEndDate, today)) {
			return 'Today';
		}
		return 'Custom';
	}


	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
